import React, { useEffect, useState } from "react";
import {
  FormGroup,
  MenuItem,
  ListItemText,
  SxProps,
  Select,
  Box,
} from "@mui/material";
import { Checkbox, Chip } from "cprime-foundational-components";

interface Application {
  app_id: number;
  app_name: string;
}

interface ValueStream {
  value_stream_id: number;
  value_stream_name: string;
  applications: Application[];
}

interface FilterCustomSelectProps {
  valueStreams: ValueStream[];
  onChange: (selectedValues: { app_id: number[] }) => void;
  selectedOptions?: { app_id: number[] };
  optionsSx?: SxProps;
  placeholder?: string; // New prop for placeholder text
}

/**
 * FilterCustomSelect - Specialized select component for FilterButton
 * Shows application options directly without value stream grouping headers.
 * Based on CustomSelect logic but simplified for dashboard filter use case.
 * Used exclusively in the Dashboard FilterButton.
 */
const FilterCustomSelect: React.FC<FilterCustomSelectProps> = ({
  valueStreams,
  onChange,
  selectedOptions,
  optionsSx,
  placeholder = "Select Applications", // Default placeholder
}) => {
  const allAppIds = valueStreams
    ?.flatMap((vs) => vs?.applications)
    ?.map((app) => app?.app_id);
  // Initialize state with prop value if available, otherwise empty array
  const [selectedValues, setSelectedValues] = useState<number[]>(
    selectedOptions?.app_id?.length ? selectedOptions.app_id : [],
  );

  // Sync selectedValues whenever selectedOptions prop changes
  useEffect(() => {
    if (selectedOptions?.app_id !== undefined) {
      setSelectedValues(selectedOptions.app_id);
    }
  }, [selectedOptions?.app_id]);

  const handleToggleApplication = (appId: number) => {
    setSelectedValues((prev: any) => {
      const newValues = prev?.includes(appId)
        ? prev?.filter((v: any) => v !== appId)
        : [...(prev || []), appId];
      updateSelection(newValues);
      return newValues;
    });
  };

  const handleSelectAll = () => {
    const newValues =
      selectedValues?.length === allAppIds?.length ? [] : allAppIds;
    setSelectedValues(newValues);
    updateSelection(newValues);
  };

  const updateSelection = (newValues: number[]) => {
    onChange({
      app_id: newValues,
    });
  };

  useEffect(() => {
    if (selectedValues?.length === 0) {
      // Replace the `&ZeroWidthSpace;` with a default text
      const zeroWidthSpaceElement = document.querySelector(
        "[data-testid='filter-custom-select'] span.notranslate",
      );

      // Check if the element exists before updating
      if (zeroWidthSpaceElement) {
        zeroWidthSpaceElement.textContent = placeholder;
      }
    }
  }, [selectedValues, placeholder]);

  return (
    <Box>
      <Select
        multiple
        data-testid="filter-custom-select"
        value={selectedValues}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: "22rem",
              width: "22rem",
              overflowY: "auto",
            },
          },
        }}
        renderValue={(selected) => {
          if (selected?.includes(-1)) return "All";
          const displayedValues = selected as number[];
          if (displayedValues?.length === 0) return placeholder;

          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Chip
                key={
                  displayedValues?.length === selectedValues?.length
                    ? "all-chip"
                    : displayedValues[displayedValues?.length - 1]
                }
                label={
                  displayedValues?.length === allAppIds?.length
                    ? "All"
                    : (() => {
                        const lastSelectedId =
                          displayedValues[displayedValues?.length - 1];
                        const app = valueStreams
                          ?.flatMap((vs) => vs?.applications)
                          ?.find((app) => app?.app_id === lastSelectedId);
                        return app ? app?.app_name : "";
                      })()
                }
                size="small"
                style={{ marginRight: 4 }}
              />
              {displayedValues?.length === allAppIds?.length
                ? "Applications"
                : ""}
              {displayedValues?.length > 1 &&
                displayedValues?.length !== allAppIds?.length && (
                  <span
                    style={{
                      color: "#000",
                      marginLeft: "auto",
                      letterSpacing: "0.00938em",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      lineHeight: "1rem",
                    }}
                  >
                    + {displayedValues?.length - 1} more
                  </span>
                )}
            </div>
          );
        }}
        sx={{
          height: 48,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "black",
          },
          ...optionsSx,
        }}
      >
        <MenuItem
          data-testid="select-all-option"
          onClick={handleSelectAll}
          sx={{ display: "flex", alignItems: "left", padding: "8px 16px" }}
        >
          <Checkbox
            checked={selectedValues?.length === allAppIds?.length}
            indeterminate={
              selectedValues?.length > 0 &&
              selectedValues?.length < allAppIds?.length
            }
          />
          <ListItemText
            primary="All"
            primaryTypographyProps={{
              noWrap: true,
              sx: { overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
        </MenuItem>
        <FormGroup>
          {valueStreams?.flatMap((valueStream) =>
            valueStream.applications.map((app) => (
              <MenuItem
                key={app.app_id}
                onClick={() => handleToggleApplication(app.app_id)}
                sx={{
                  display: "flex",
                  alignItems: "left",
                  width: "100%",
                  padding: "8px 16px",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Checkbox checked={selectedValues?.includes(app.app_id)} />
                <ListItemText
                  primary={app.app_name}
                  sx={{
                    color: "#000",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              </MenuItem>
            )),
          )}
        </FormGroup>
      </Select>
    </Box>
  );
};

export default FilterCustomSelect;
